/**
 * The servers a person can point the app at — and the rule for who edits what.
 *
 * Two kinds of entry. SYSTEM ones ship inside the binary: they are the
 * addresses the project itself stands behind (CORE under its own name, and
 * the public one), so a person may pick them but never rewrite them — an
 * address that can be edited in a settings screen can be edited by whoever
 * gets to that screen, and a pinned CORE (ADR-018) only means something if
 * its name cannot be quietly swapped. CUSTOM ones a person adds by hand: a
 * relay they were given, their own server, a test box. Those are theirs to
 * add and remove, except the one currently in use — removing the address
 * the app is talking to would leave it talking to nothing.
 *
 * This module holds the rules; it owns no storage and touches no window, so
 * the rules are testable and the screen stays a screen. The active address
 * is the shell's (window.__VOXIOX_SERVER_URL__, see serverBase.mjs): changing
 * it goes through the native bridge, because only the shell can restart the
 * embedded UI against a new origin.
 */

export const SYSTEM_SERVERS = Object.freeze([
  Object.freeze({ url: 'https://core.voxiox.com', label: 'Voxiox CORE' }),
  Object.freeze({ url: 'https://voxiox.com', label: 'Voxiox' }),
]);

/** localStorage key for the person's own list — an array of origins. */
export const CUSTOM_SERVERS_KEY = 'voxiox_custom_servers';

/**
 * An address is an ORIGIN: scheme + host [+ port]. No path, no query, no
 * credentials — the app appends /api, /ws and the SIP domain itself, and a
 * path would silently break each of them differently. Plain http is allowed
 * only for a loopback box: a relay reached over http is not a relay.
 */
export function normalizeServerUrl(raw) {
  let s = String(raw ?? '').trim();
  if (!s) return null;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) s = 'https://' + s;
  let u;
  try { u = new URL(s); } catch (e) { return null; }
  if (!u.hostname) return null;
  if (u.username || u.password) return null;
  if ((u.pathname && u.pathname !== '/') || u.search || u.hash) return null;
  if (u.protocol === 'https:') return u.origin;
  if (u.protocol === 'http:' && isLoopback(u.hostname)) return u.origin;
  return null;
}

function isLoopback(host) {
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
}

export function isSystemServer(url) {
  return SYSTEM_SERVERS.some((s) => s.url === url);
}

/** "core.voxiox.com" — what a row shows when the address has no name. */
export function hostLabel(url) {
  try { return new URL(url).host; } catch (e) { return String(url || ''); }
}

export function readCustomServers(storage) {
  try {
    const v = JSON.parse(storage.getItem(CUSTOM_SERVERS_KEY) || '[]');
    if (!Array.isArray(v)) return [];
    const out = [];
    for (const x of v) {
      // A number here parses as an IPv4 address (42 → 0.0.0.42): only text.
      const n = typeof x === 'string' ? normalizeServerUrl(x) : null;
      if (n && !isSystemServer(n) && !out.includes(n)) out.push(n);
    }
    return out;
  } catch (e) { return []; }
}

export function writeCustomServers(storage, list) {
  storage.setItem(CUSTOM_SERVERS_KEY, JSON.stringify(list));
}

/**
 * What the screen shows: system entries first, then the person's own, the
 * active one flagged. An active address that belongs to neither list — the
 * one typed at first setup, before this screen existed — is shown among the
 * custom ones, so the list never says "none of these" about the server the
 * app is using right now.
 */
export function serverList({ active, custom = [] }) {
  const a = normalizeServerUrl(active);
  const seen = new Set();
  const out = [];
  for (const s of SYSTEM_SERVERS) {
    seen.add(s.url);
    out.push({ url: s.url, label: s.label, system: true, active: s.url === a, removable: false });
  }
  const extra = [...custom];
  if (a && !seen.has(a) && !extra.includes(a)) extra.push(a);
  for (const url of extra) {
    if (seen.has(url)) continue;
    seen.add(url);
    out.push({ url, label: hostLabel(url), system: false, active: url === a, removable: url !== a });
  }
  return out;
}

export function addCustomServer(custom, raw) {
  const url = normalizeServerUrl(raw);
  if (!url) return { ok: false, reason: 'Нужен адрес вида https://хост — без пути и параметров' };
  if (isSystemServer(url)) return { ok: false, reason: 'Этот адрес уже есть среди системных' };
  if (custom.includes(url)) return { ok: false, reason: 'Этот адрес уже добавлен' };
  return { ok: true, url, custom: [...custom, url] };
}

export function removeCustomServer(custom, url, active) {
  if (isSystemServer(url)) return { ok: false, reason: 'Системный адрес убрать нельзя' };
  if (normalizeServerUrl(active) === url) return { ok: false, reason: 'Сначала переключитесь на другой сервер' };
  if (!custom.includes(url)) return { ok: false, reason: 'Такого адреса нет в списке' };
  return { ok: true, custom: custom.filter((u) => u !== url) };
}

/**
 * Whether the app can switch at all: only a native shell can restart the
 * embedded UI against another origin. In a browser the page IS the server,
 * and "switching" would be a navigation, not a setting.
 */
export function canSwitchServer(win = typeof window === 'undefined' ? undefined : window) {
  return typeof win?.VoxioxNative?.setServerURL === 'function';
}

/** Ask the shell to switch. Returns false when there is no shell to ask. */
export function switchServer(url, win = typeof window === 'undefined' ? undefined : window) {
  const n = normalizeServerUrl(url);
  if (!n || !canSwitchServer(win)) return false;
  win.VoxioxNative.setServerURL(n);
  return true;
}
